import React from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { Card } from '@/components/ui/card';

const COLORS = {
  high: '#2E7D32',
  medium: '#F57F17',
  low: '#C62828',
};

const getColor = (val) => {
  if (val >= 7) return COLORS.high;
  if (val >= 4) return COLORS.medium;
  return COLORS.low;
};

export const IntegrityRadarChart = ({ candidate }) => {
  const legal = candidate.legal_status || {};
  const political = candidate.political_history || {};
  const assets = candidate.assets || {};
  const reputation = candidate.reputation || {};

  // Derive sub-scores from data quality/content
  const legalScore = legal.ficha_limpa_compliant ? 8 : 3;
  const processScore = (legal.processes || []).some(p => p.toLowerCase().includes('nenhum')) ? 9 : 4;
  const convictionScore = (legal.convictions || []).some(c => c.toLowerCase().includes('nenhum')) ? 10 : 2;
  const controversyScore = (political.controversies || []).some(c => c.toLowerCase().includes('nenhum')) ? 9 : 4;
  const inconsistencyScore = (assets.inconsistencies || []).some(i => i.toLowerCase().includes('nenhum')) ? 9 : 4;

  const radarData = [
    { subject: 'Ficha Limpa', value: legalScore, fullMark: 10 },
    { subject: 'Processos', value: processScore, fullMark: 10 },
    { subject: 'Condenações', value: convictionScore, fullMark: 10 },
    { subject: 'Controvérsias', value: controversyScore, fullMark: 10 },
    { subject: 'Patrimônio', value: inconsistencyScore, fullMark: 10 },
    { subject: 'Score Geral', value: candidate.integrity_score || 5, fullMark: 10 },
  ];

  return (
    <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="integrity-radar-chart">
      <h3 className="text-lg font-bold text-[#0F1C2E] mb-4">Radar de Integridade</h3>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={radarData}>
          <PolarGrid stroke="#E2E8F0" />
          <PolarAngleAxis
            dataKey="subject"
            tick={{ fontSize: 11, fill: '#475569' }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 10]}
            tick={{ fontSize: 10, fill: '#94A3B8' }}
          />
          <Radar
            name="Integridade"
            dataKey="value"
            stroke="#1E3A5F"
            fill="#1E3A5F"
            fillOpacity={0.15}
            strokeWidth={2}
          />
        </RadarChart>
      </ResponsiveContainer>
    </Card>
  );
};

export const ScoreBreakdownChart = ({ candidate }) => {
  const score = candidate.integrity_score || 5;
  const legal = candidate.legal_status || {};

  const data = [
    { name: 'Jurídico', value: legal.ficha_limpa_compliant ? 8.5 : 3 },
    { name: 'Político', value: Math.min(10, score + 0.5) },
    { name: 'Patrimônio', value: Math.min(10, score - 0.3) },
    { name: 'Reputação', value: Math.min(10, score + 0.2) },
    { name: 'Geral', value: score },
  ];

  return (
    <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="score-breakdown-chart">
      <h3 className="text-lg font-bold text-[#0F1C2E] mb-4">Score por Criterio</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11, fill: '#94A3B8' }} />
          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11, fill: '#475569' }} />
          <Tooltip
            formatter={(value) => [`${value.toFixed(1)}/10`, 'Score']}
            contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={getColor(entry.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

export const WeightBreakdownChart = () => {
  const weights = [
    { name: 'Condenações', peso: 40, fill: '#C62828' },
    { name: 'Processos', peso: 20, fill: '#F57F17' },
    { name: 'Histórico', peso: 15, fill: '#1E3A5F' },
    { name: 'Patrimônio', peso: 15, fill: '#2E7D32' },
    { name: 'Reputação', peso: 10, fill: '#475569' },
  ];

  return (
    <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="weight-breakdown-chart">
      <h3 className="text-lg font-bold text-[#0F1C2E] mb-2">Peso dos Criterios</h3>
      <p className="text-xs text-[#94A3B8] mb-4">Como o score de integridade é calculado</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={weights} margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} />
          <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} domain={[0, 50]} unit="%" />
          <Tooltip
            formatter={(value) => [`${value}%`, 'Peso']}
            contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
          />
          <Bar dataKey="peso" radius={[4, 4, 0, 0]} barSize={36}>
            {weights.map((entry, idx) => (
              <Cell key={idx} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};
